import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Edit2, LogOut, Crown, Eye, EyeOff, X, Plus, Upload, Loader2, Lock, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, uploadPhoto } from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser, isPremium } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    gender: user?.gender || '',
    location: user?.location || '',
    bio: user?.bio || '',
    interests: user?.interests || [],
    looking_for: user?.looking_for || '',
    photos: user?.photos || [],
    private_photos: user?.private_photos || [],
    profile_hidden: user?.profile_hidden || false,
    photo_blurred: user?.photo_blurred || false
  });
  const [uploadingPrivate, setUploadingPrivate] = useState(false);
  const privateFileInputRef = useRef(null);

  const defaultPhoto = useDefaultAvatar() || 'https://images.unsplash.com/photo-1650702970095-f5b8ebeebce5?w=400';

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await updateProfile({
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      });
      updateUser(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setError('Please upload a valid image (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }

    if (formData.photos.length >= 6) {
      setError('Maximum 6 photos allowed');
      return;
    }

    setUploadingPhoto(true);
    setError('');

    try {
      const response = await uploadPhoto(file);
      const photoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, photoUrl]
      }));
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handlePrivatePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setError('Please upload a valid image');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }
    if (formData.private_photos.length >= 6) {
      setError('Maximum 6 private photos allowed');
      return;
    }
    setUploadingPrivate(true);
    setError('');
    try {
      const response = await uploadPhoto(file);
      const photoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, private_photos: [...prev.private_photos, photoUrl] }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPrivate(false);
      if (privateFileInputRef.current) privateFileInputRef.current.value = '';
    }
  };

  const removePrivatePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      private_photos: prev.private_photos.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="profile-page">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        data-testid="photo-file-input"
      />
      <input
        type="file"
        ref={privateFileInputRef}
        onChange={handlePrivatePhotoUpload}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        data-testid="private-photo-file-input"
      />

      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Profile</h1>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditing(true)}
                className="rounded-full"
                data-testid="edit-profile-btn"
              >
                <Edit2 className="w-5 h-5 text-gray-600" />
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
                  data-testid="save-profile-btn"
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-16 px-4">
        <div className="max-w-lg mx-auto">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Profile Header */}
          <div className="bg-white rounded-3xl p-6 mb-4 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="relative">
                <img
                  src={user?.photos?.[user?.primary_photo_index ?? 0] || user?.photos?.[0] || defaultPhoto}
                  alt={user?.name}
                  className="w-24 h-24 rounded-full object-cover"
                />
                {isPremium && (
                  <div className="absolute -bottom-1 -right-1 premium-badge flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    PRO
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  {user?.display_name || user?.name}, {user?.age || '??'}
                </h2>
                {(user?.current_city || user?.location) && (
                  <div className="flex items-center gap-1 text-gray-500 mt-1">
                    <MapPin className="w-4 h-4" />
                    <span>
                      {user?.current_city
                        ? [user.current_city, user.current_country].filter(Boolean).join(', ')
                        : user?.location}
                    </span>
                  </div>
                )}
                {!isPremium && (
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="mt-3 bg-gradient-to-r from-[#FFCE00] to-[#FFB800] text-[#0a0a0a] font-semibold rounded-full text-sm"
                    size="sm"
                    data-testid="upgrade-btn"
                  >
                    <Crown className="w-4 h-4 mr-1" />
                    Upgrade to Premium
                  </Button>
                )}
              </div>
            </div>

            {/* Completion */}
            {typeof user?.completion_percentage === 'number' && user.completion_percentage < 100 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Profile Completion</span>
                  <span className="text-sm font-semibold text-[#009E60]">{user.completion_percentage}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#009E60] transition-all duration-500"
                    style={{ width: `${user.completion_percentage}%` }}
                  />
                </div>
                <button
                  onClick={() => navigate('/profile/setup')}
                  className="text-xs text-[#009E60] font-medium mt-2 hover:underline"
                  data-testid="complete-profile-btn"
                >
                  Complete your profile →
                </button>
              </div>
            )}

            {/* Stats */}
            <div className="flex gap-6 mt-6 pt-6 border-t border-gray-100">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {(user?.credits || 0) + (user?.promo_credits || 0)}
                </div>
                <div className="text-xs text-gray-500">
                  Credits
                  {(user?.promo_credits || 0) > 0 && (
                    <span className="text-[#009E60] font-medium"> · {user.promo_credits} bonus</span>
                  )}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">--</div>
                <div className="text-xs text-gray-500">Matches</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">--</div>
                <div className="text-xs text-gray-500">Likes</div>
              </div>
            </div>
          </div>

          {/* Edit Form / Display */}
          {isEditing ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-6 mb-4 shadow-sm space-y-4"
            >
              {/* Photos */}
              <div>
                <Label className="text-gray-700 font-semibold">Photos</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="aspect-square relative">
                      {formData.photos[i] ? (
                        <div className="w-full h-full rounded-xl overflow-hidden relative group">
                          <img
                            src={formData.photos[i]}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removePhoto(i)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingPhoto}
                          className="w-full h-full rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center hover:border-[#009E60] transition-colors disabled:opacity-50"
                        >
                          {uploadingPhoto && i === formData.photos.length ? (
                            <Loader2 className="w-5 h-5 text-[#009E60] animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Private Photos */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="w-4 h-4 text-gray-700" />
                  <Label className="text-gray-700 font-semibold">Private photos</Label>
                  <span className="text-xs text-gray-400">({formData.private_photos.length}/6)</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">Locked by default. Members must request access before they can see these.</p>
                <div className="grid grid-cols-3 gap-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="aspect-square relative">
                      {formData.private_photos[i] ? (
                        <div className="w-full h-full rounded-xl overflow-hidden relative group border-2 border-gray-300">
                          <img
                            src={formData.private_photos[i]}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-1 left-1 bg-black/60 rounded-full p-1">
                            <Lock className="w-3 h-3 text-white" />
                          </div>
                          <button
                            onClick={() => removePrivatePhoto(i)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                            data-testid={`remove-private-photo-${i}`}
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => privateFileInputRef.current?.click()}
                          disabled={uploadingPrivate}
                          className="w-full h-full rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center hover:border-[#009E60] transition-colors disabled:opacity-50"
                          data-testid={`add-private-photo-${i}`}
                        >
                          {uploadingPrivate && i === formData.private_photos.length ? (
                            <Loader2 className="w-5 h-5 text-[#009E60] animate-spin" />
                          ) : (
                            <Lock className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-gray-700">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1.5 rounded-xl"
                  data-testid="edit-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Age</Label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                    className="mt-1.5 rounded-xl"
                    data-testid="edit-age"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData(prev => ({ ...prev, gender: v }))}>
                    <SelectTrigger className="mt-1.5 rounded-xl">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-gray-700">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="mt-1.5 rounded-xl"
                  data-testid="edit-location"
                />
              </div>

              <div>
                <Label className="text-gray-700">Bio</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  className="mt-1.5 rounded-xl resize-none"
                  rows={4}
                  data-testid="edit-bio"
                />
              </div>

              <div>
                <Label className="text-gray-700">Looking for</Label>
                <Select value={formData.looking_for} onValueChange={(v) => setFormData(prev => ({ ...prev, looking_for: v }))}>
                  <SelectTrigger className="mt-1.5 rounded-xl">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relationship">Relationship</SelectItem>
                    <SelectItem value="friendship">Friendship</SelectItem>
                    <SelectItem value="casual">Something Casual</SelectItem>
                    <SelectItem value="unsure">Not Sure Yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </motion.div>
          ) : (
            <div className="bg-white rounded-3xl p-6 mb-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">About Me</h3>
              <p className="text-gray-600">
                {user?.bio || 'No bio yet. Tap edit to add one!'}
              </p>

              {user?.prompts?.length > 0 && (
                <div className="mt-4 space-y-3">
                  {user.prompts.map((p, i) => (
                    p.answer && (
                      <div key={i} className="bg-[#009E60]/5 rounded-2xl p-4">
                        <div className="text-xs font-semibold text-[#009E60] mb-1">{p.question}</div>
                        <div className="text-gray-800">{p.answer}</div>
                      </div>
                    )
                  ))}
                </div>
              )}

              {user?.interests?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Interests</h4>
                  <div className="flex flex-wrap gap-2">
                    {user.interests.map((interest, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-[#009E60]/10 text-[#009E60] rounded-full text-sm"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {user?.personality?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Personality</h4>
                  <div className="flex flex-wrap gap-2">
                    {user.personality.map((trait, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-[#FFCE00]/20 text-gray-800 rounded-full text-sm"
                      >
                        {trait}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                {user?.looking_for && (
                  <div>
                    <div className="text-xs text-gray-500">Looking for</div>
                    <div className="text-gray-800 capitalize">{user.looking_for.replace(/-/g, ' ')}</div>
                  </div>
                )}
                {user?.interested_in && (
                  <div>
                    <div className="text-xs text-gray-500">Interested in</div>
                    <div className="text-gray-800 capitalize">{user.interested_in}</div>
                  </div>
                )}
                {user?.ideal_date && (
                  <div>
                    <div className="text-xs text-gray-500">Ideal date</div>
                    <div className="text-gray-800 capitalize">{user.ideal_date.replace(/-/g, ' ')}</div>
                  </div>
                )}
                {user?.has_children && (
                  <div>
                    <div className="text-xs text-gray-500">Children</div>
                    <div className="text-gray-800 capitalize">{user.has_children.replace(/-/g, ' ')}</div>
                  </div>
                )}
                {user?.wants_children && (
                  <div>
                    <div className="text-xs text-gray-500">Wants children</div>
                    <div className="text-gray-800 capitalize">{user.wants_children.replace(/-/g, ' ')}</div>
                  </div>
                )}
                {user?.smoking && (
                  <div>
                    <div className="text-xs text-gray-500">Smoking</div>
                    <div className="text-gray-800 capitalize">{user.smoking.replace(/-/g, ' ')}</div>
                  </div>
                )}
                {user?.drinking && (
                  <div>
                    <div className="text-xs text-gray-500">Drinking</div>
                    <div className="text-gray-800 capitalize">{user.drinking.replace(/-/g, ' ')}</div>
                  </div>
                )}
                {user?.from_country && (
                  <div>
                    <div className="text-xs text-gray-500">From</div>
                    <div className="text-gray-800">
                      {[user.from_city, user.from_country].filter(Boolean).join(', ')}
                    </div>
                  </div>
                )}
              </div>

              {user?.relationship_priorities?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">What I value</h4>
                  <div className="flex flex-wrap gap-2">
                    {user.relationship_priorities.map((p, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-[#E2231A]/10 text-[#E2231A] rounded-full text-sm"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings */}
          <div className="bg-white rounded-3xl p-6 mb-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Settings</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {user?.profile_hidden ? (
                    <EyeOff className="w-5 h-5 text-gray-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500" />
                  )}
                  <div>
                    <div className="font-medium text-gray-900">Hide Profile</div>
                    <div className="text-sm text-gray-500">Others won't see you in discovery</div>
                  </div>
                </div>
                <Switch
                  checked={formData.profile_hidden}
                  onCheckedChange={(checked) => {
                    setFormData(prev => ({ ...prev, profile_hidden: checked }));
                    updateProfile({ profile_hidden: checked });
                  }}
                  data-testid="hide-profile-switch"
                />
              </div>

              {/* Photo blur toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {user?.photo_blurred ? (
                    <EyeOff className="w-5 h-5 text-[#009E60]" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500" />
                  )}
                  <div>
                    <div className="font-medium text-gray-900">Blur My Main Photo</div>
                    <div className="text-sm text-gray-500">
                      {user?.photo_blurred
                        ? 'Members see a blurred version of your photo'
                        : 'Everyone can see your photo clearly'}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={formData.photo_blurred || false}
                  onCheckedChange={async (checked) => {
                    setFormData(prev => ({ ...prev, photo_blurred: checked }));
                    try {
                      const res = await updateProfile({ photo_blurred: checked });
                      updateUser(res.data);
                    } catch (err) {
                      console.error('Failed to toggle blur:', err);
                    }
                  }}
                  data-testid="photo-blur-switch"
                />
              </div>

              <button
                onClick={() => navigate('/profile/setup')}
                className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="edit-full-profile-btn"
              >
                <Edit2 className="w-5 h-5 text-[#009E60]" />
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Edit Full Profile</div>
                  <div className="text-sm text-gray-500">Update lifestyle, personality, and more</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/pricing')}
                className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Crown className="w-5 h-5 text-[#FFCE00]" />
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">Premium</div>
                  <div className="text-sm text-gray-500">
                    {isPremium ? 'Manage your subscription' : 'Upgrade for unlimited features'}
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/contact')}
                className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-gray-50 text-gray-700 transition-colors"
                data-testid="contact-us-menu-btn"
              >
                <Mail className="w-5 h-5" />
                <span className="font-medium">Contact us</span>
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ProfilePage;
